import React, { useState, useEffect, useRef, useCallback } from 'react';
import useGetListLogContainerApp from 'redux/slices/containerApp/hooks/useGetListLogContainerApp';
import { useParams } from 'react-router-dom';
import {
  Box,
  Pagination,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  InputLabel,
  FormControl,
  Alert,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import {
  inputFormatDateTime,
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_SIZE,
} from '@tenant/utility/constants/default';
import { DateMenu } from '@tenant/core/App/Date/DateMenu';
import useNextTick from '@tenant/utility/hooks/useNextTick';
import moment from 'moment';
import { STATISTIC_DATE_GROUP_TYPE } from '@tenant/utility/constants/enum';
import LogList from 'pages/containerApp/Common/LogList';
import FooterWrapper from '@tenant/core/AppLayout/components/AppFooter/FooterWrapper';
import {
  DEFAULT_LOG_RANGE_NAME,
  resolveLogRange,
} from 'pages/containerApp/Detail/Tab/logRangePresets';

const defaultRangeName = DEFAULT_LOG_RANGE_NAME;
// Presentation plumbing for DateMenu only — the option table drives the menu's labels
// and descriptions. The window actually sent to the API is NOT read from here: it is
// resolved from the selected preset key at query-build time via resolveLogRange, so it
// is fresh on every query and on every auto-refresh poll. These `value` entries are
// evaluated once at module load and would otherwise freeze the window for the lifetime
// of the JS chunk. The multi-day presets carry a time component (inputFormatDateTime,
// not the date-only inputFormat) so nothing here implies a midnight-snapped range.
const dataOptions = {
  'Last 30 minutes': {
    value: [
      moment().subtract(30, 'minutes').format(inputFormatDateTime),
      moment().format(inputFormatDateTime),
    ],
    description: `Last 30 minutes`,
    key: 'hours',
    groupType: STATISTIC_DATE_GROUP_TYPE.Day,
    title: 'Recent',
    text: 'Last 30 minutes',
  },
  'Last 1 hour': {
    value: [
      moment().subtract(1, 'hours').format(inputFormatDateTime),
      moment().format(inputFormatDateTime),
    ],
    description: `Last 1 hour`,
    key: 'hours',
    groupType: STATISTIC_DATE_GROUP_TYPE.Day,
    title: 'Recent',
    text: 'Last 1 hour',
  },
  'Last 4 hours': {
    value: [
      moment().subtract(4, 'hours').format(inputFormatDateTime),
      moment().format(inputFormatDateTime),
    ],
    description: `Last 4 hours`,
    key: 'hours',
    groupType: STATISTIC_DATE_GROUP_TYPE.Day,
    title: 'Recent',
    text: 'Last 4 hours',
  },
  'Last 12 hours': {
    value: [
      moment().subtract(12, 'hours').format(inputFormatDateTime),
      moment().format(inputFormatDateTime),
    ],
    description: `Last 12 hours`,
    key: 'hours',
    groupType: STATISTIC_DATE_GROUP_TYPE.Day,
    title: 'Recent',
    text: 'Last 12 hours',
  },
  'Last 1 day': {
    value: [
      moment().subtract(1, 'days').format(inputFormatDateTime),
      moment().format(inputFormatDateTime),
    ],
    description: `${moment().subtract(1, 'days').format('MMM')} ${moment()
      .subtract(1, 'days')
      .format('DD')}-${moment().format('MMM')} ${moment().format('DD')}`,
    key: 'days',
    groupType: STATISTIC_DATE_GROUP_TYPE.Day,
    title: 'Relative dates',
    text: 'Last 1 day',
  },
  'Last 3 days': {
    value: [
      moment().subtract(3, 'days').format(inputFormatDateTime),
      moment().format(inputFormatDateTime),
    ],
    description: `${moment().subtract(3, 'days').format('MMM')} ${moment()
      .subtract(3, 'days')
      .format('DD')}-${moment().format('MMM')} ${moment().format('DD')}`,
    key: 'days',
    groupType: STATISTIC_DATE_GROUP_TYPE.Day,
    title: 'Relative dates',
    text: 'Last 3 days',
  },
  'Last 7 days': {
    value: [
      moment().subtract(7, 'days').format(inputFormatDateTime),
      moment().format(inputFormatDateTime),
    ],
    description: `${moment().subtract(7, 'days').format('MMM')} ${moment()
      .subtract(7, 'days')
      .format('DD')}-${moment().format('MMM')} ${moment().format('DD')}`,
    key: 'days',
    groupType: STATISTIC_DATE_GROUP_TYPE.Day,
    title: 'Relative dates',
    text: 'Last 7 days',
  },
  'Last 30 days': {
    value: [
      moment().subtract(30, 'days').format(inputFormatDateTime),
      moment().format(inputFormatDateTime),
    ],
    description: `${moment().subtract(30, 'days').format('MMM')} ${moment()
      .subtract(30, 'days')
      .format('DD')}-${moment().format('MMM')} ${moment().format('DD')}`,
    key: 'days',
    groupType: STATISTIC_DATE_GROUP_TYPE.Day,
    title: 'Relative dates',
    text: 'Last 30 days',
  },
};

const LOG_LEVEL_OPTIONS = [
  'INFO',
  'WARNING',
  'ERROR',
  'DEBUG',
  'TRACE',
  'EXCEPTION',
  'FAILURE',
  'OTHER',
];

const REFRESH_INTERVAL_OPTIONS = [
  { label: '30 seconds', value: 30 },
  { label: '1 minute', value: 60 },
];

const MAX_KEYWORD_LENGTH = 1000;

const Logs = () => {
  const nextTick = useNextTick();
  const { log, logFilter } = useSelector((s) => s.containerApp);
  const [rangeDate, setRangeDate] = useState(defaultRangeName);

  // The window sent to the API, resolved from the selected preset key rather than read
  // from the module-scope option table. Held in state (not recomputed per render) because
  // the query key is a JSON.stringify of these values — recomputing every render would
  // change the key every render and refetch forever. It is refreshed at each point a query
  // is actually fired, which is what makes the preset genuinely rolling.
  const [range, setRange] = useState(() => resolveLogRange(defaultRangeName));

  // The interval closure below is created once per startAutoRefresh call, so it would
  // capture whichever rangeDate was current then. Mirror the existing refetchRef pattern
  // so each poll resolves against the preset the user actually has selected.
  const rangeDateRef = useRef(rangeDate);
  useEffect(() => {
    rangeDateRef.current = rangeDate;
  }, [rangeDate]);

  // Read-only now: the date range is no longer written back into filter (it is resolved
  // from the preset key instead), so this only forwards the persisted non-date fields.
  const [filter] = useState(logFilter ? { ...logFilter } : {});
  const [pageIndex, setPageIndex] = useState(
    logFilter?.pageIndex ?? DEFAULT_PAGE_INDEX,
  );

  // Keyword search state — keywordInput is the live text field value;
  // activeKeyword is what was last submitted and drives the query.
  const [keywordInput, setKeywordInput] = useState(logFilter?.keyword ?? '');
  const [activeKeyword, setActiveKeyword] = useState(logFilter?.keyword ?? '');

  // Log level filter state — pendingLevels tracks checkbox ticks while the
  // dropdown is open; selectedLevels is committed on dropdown close and drives the query.
  const [pendingLevels, setPendingLevels] = useState(logFilter?.logLevels ?? []);
  const [selectedLevels, setSelectedLevels] = useState(logFilter?.logLevels ?? []);

  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [refreshError, setRefreshError] = useState(null);
  const intervalRef = useRef(null);

  // Keep a stable ref to refetch so the setInterval closure always calls the
  // latest version even after the query key changes due to filter updates. (M-2)
  const refetchRef = useRef(null);

  const { appInstanceId, id: containerAppId } = useParams();
  const { isLoading, isRefetching, isError, refetch } = useGetListLogContainerApp({
    ...filter,
    appInstanceId,
    containerAppId,
    // Deliberately AFTER the ...filter spread. filter is seeded from the persisted redux
    // logFilter, which carries the startDate/endDate of the previous visit — spreading it
    // last would let a stale window override the freshly resolved one on remount.
    startDate: range.startDate,
    endDate: range.endDate,
    currentPage: pageIndex,
    pageSize: DEFAULT_PAGE_SIZE,
    keyword: activeKeyword,
    logLevels: selectedLevels,
  });

  // Sync the latest refetch into the ref so the interval always uses it. (M-2)
  useEffect(() => {
    refetchRef.current = refetch;
  });

  // Clear auto-refresh interval on unmount (AC-16)
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const stopAutoRefresh = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startAutoRefresh = useCallback(
    (intervalSeconds) => {
      stopAutoRefresh();
      intervalRef.current = setInterval(() => {
        // auto-refresh spec AC-17: reset to page 1 on each poll
        setPageIndex(DEFAULT_PAGE_INDEX);
        // Re-resolve the window so the preset actually rolls. Without this the poll
        // re-queries the window that was current when the tab was first opened, and
        // drifts further out of date the longer the tab stays mounted.
        //
        // This IS the fetch trigger — no explicit refetch here. resolveLogRange returns
        // ms-precision instants, so every poll produces a new react-query key and the
        // (ungated) query fetches on the key change alone. Calling refetch in the same
        // tick would fire a SECOND request against the PREVIOUS key, because React 18
        // has not flushed setRange yet and the observer still points at the old key.
        setRange(resolveLogRange(rangeDateRef.current));
      }, intervalSeconds * 1000);
    },
    [stopAutoRefresh],
  );

  // The poll no longer calls refetch itself (the range change is the trigger), so nothing
  // throws inside the interval and the error banner can no longer be raised from a catch.
  // Derive it from the query's own error state instead while auto-refresh is on.
  // auto-refresh spec AC-18
  useEffect(() => {
    if (!autoRefresh) return;
    setRefreshError(isError ? 'Auto-refresh failed — retrying' : null);
  }, [isError, autoRefresh]);

  // Restart auto-refresh whenever the interval value changes while auto-refresh is on
  useEffect(() => {
    if (autoRefresh) {
      startAutoRefresh(refreshInterval);
    }
    // startAutoRefresh is stable (stopAutoRefresh dep only); refreshInterval is
    // intentionally the only trigger here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshInterval]);

  // fireQueryAndRestartRefresh accepts the new filter values directly so it can
  // call refetch immediately after state is applied via nextTick, avoiding the
  // async-state race where the query would see the previous render's values.
  const fireQueryAndRestartRefresh = useCallback(
    async (presetKey = undefined) => {
      stopAutoRefresh();
      // Re-resolve the window on every query the user fires, not just on the date pick.
      // The tab can sit mounted for hours before someone types a keyword, and a window
      // anchored to when the tab was opened would be stale by exactly that long.
      // presetKey is passed explicitly by the date-pick path, whose setRangeDate has not
      // been flushed to rangeDateRef yet at this point.
      setRange(resolveLogRange(presetKey ?? rangeDateRef.current));
      // nextTick waits for React to flush the pending state updates so that the
      // query hook re-renders with the new values before refetch is called.
      await nextTick();
      try {
        await refetchRef.current?.();
        setRefreshError(null);
      } catch {
        setRefreshError('Auto-refresh failed — retrying');
      }
      if (autoRefresh) {
        startAutoRefresh(refreshInterval);
      }
    },
    [stopAutoRefresh, nextTick, autoRefresh, refreshInterval, startAutoRefresh],
  );

  const handleChangeDate = async (data) => {
    const { keyName } = data || {};
    setRangeDate(keyName);
    setPageIndex(DEFAULT_PAGE_INDEX);
    // The startDate/endDate DateMenu hands back are read from the module-scope option
    // table, so they are the values from when the JS chunk was first evaluated — even
    // re-picking a preset replays them. Resolve from the key instead, at query time.
    await fireQueryAndRestartRefresh(keyName);
  };

  const handleKeywordSearch = async () => {
    // Commit the input value to activeKeyword so the query hook picks it up after
    // nextTick inside fireQueryAndRestartRefresh flushes the state update.
    setActiveKeyword(keywordInput);
    setPageIndex(DEFAULT_PAGE_INDEX);
    await fireQueryAndRestartRefresh();
  };

  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleKeywordSearch();
    }
  };

  // Track checkbox ticks locally while the dropdown is open — do not fire a query yet.
  const handleLevelChange = (event) => {
    const value = event.target.value;
    setPendingLevels(typeof value === 'string' ? value.split(',') : value);
  };

  // Commit the selection and fire the query only when the dropdown closes. (B-2)
  const handleLevelClose = async () => {
    setSelectedLevels(pendingLevels);
    setPageIndex(DEFAULT_PAGE_INDEX);
    await fireQueryAndRestartRefresh();
  };

  const handleAutoRefreshToggle = (e) => {
    const enabled = e.target.checked;
    setAutoRefresh(enabled);
    if (enabled) {
      startAutoRefresh(refreshInterval);
    } else {
      stopAutoRefresh();
      setRefreshError(null);
    }
  };

  const handleIntervalChange = (e) => {
    const newInterval = Number(e.target.value);
    setRefreshInterval(newInterval);
    // startAutoRefresh is called via the useEffect above that watches refreshInterval
  };

  const onPageChange = (value) => {
    setPageIndex(value);
  };

  return (
    <>
      {/* Toolbar row 1: keyword search + log level filter */}
      <Box display='flex' gap={2} alignItems='center' mb={1} flexWrap='wrap'>
        <TextField
          label='Search logs'
          size='small'
          value={keywordInput}
          onChange={(e) => setKeywordInput(e.target.value)}
          onKeyDown={handleKeywordKeyDown}
          placeholder='Type keyword and press Enter'
          inputProps={{ maxLength: MAX_KEYWORD_LENGTH }}
          sx={{ minWidth: 240 }}
        />
        <Button
          variant='outlined'
          size='small'
          onClick={handleKeywordSearch}
          aria-label='Submit keyword search'
        >
          Search
        </Button>

        <FormControl size='small' sx={{ minWidth: 200 }}>
          <InputLabel id='log-level-label'>Log levels</InputLabel>
          <Select
            labelId='log-level-label'
            id='log-level-select'
            multiple
            value={pendingLevels}
            onChange={handleLevelChange}
            onClose={handleLevelClose}
            input={<OutlinedInput label='Log levels' />}
            renderValue={(selected) =>
              selected.length === 0 ? 'All levels' : selected.join(', ')
            }
            inputProps={{ 'aria-label': 'Log level filter' }}
          >
            {LOG_LEVEL_OPTIONS.map((level) => (
              <MenuItem key={level} value={level}>
                <Checkbox checked={pendingLevels.indexOf(level) > -1} />
                <ListItemText primary={level} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Toolbar row 2: date picker + auto-refresh controls */}
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb={1}
        flexWrap='wrap'
        gap={2}
      >
        <Box display='flex' alignItems='center' gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={handleAutoRefreshToggle}
                inputProps={{ 'aria-label': 'Auto-refresh toggle' }}
              />
            }
            label='Auto-refresh'
          />

          {/* Interval selector is always visible when auto-refresh is on (AC-19) */}
          {autoRefresh && (
            <FormControl size='small' sx={{ minWidth: 140 }}>
              <InputLabel id='refresh-interval-label'>Interval</InputLabel>
              <Select
                labelId='refresh-interval-label'
                value={refreshInterval}
                label='Interval'
                onChange={handleIntervalChange}
                inputProps={{ 'aria-label': 'Auto-refresh interval' }}
              >
                {REFRESH_INTERVAL_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        <DateMenu
          defaultValue={rangeDate}
          handleChangeValue={(data) => handleChangeDate(data)}
          dataOptions={dataOptions}
          filterBy={['hours', 'days']}
          isCustomDateRange={false}
        />
      </Box>

      {/* Auto-refresh error notice (auto-refresh spec AC-18) */}
      {refreshError && (
        <Alert severity='warning' sx={{ mb: 1 }}>
          {refreshError}
        </Alert>
      )}

      <LogList data={log?.Data ?? []} loading={isLoading || isRefetching} />

      <FooterWrapper className='footer'>
        <Box justifyContent='center' className='footerContainer'>
          <Pagination
            count={Math.ceil(log.TotalCount / DEFAULT_PAGE_SIZE) || 1}
            page={pageIndex}
            onChange={(e, v) => onPageChange(v)}
            showFirstButton
            showLastButton
          />
        </Box>
      </FooterWrapper>
    </>
  );
};

export default React.memo(Logs);

Logs.propTypes = {};
